import { CustomButton } from "@/components/custom/custom-button";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import CustomPagination from "@/components/custom/custom-pagination";
import { DataTable } from "@/components/custom/data-table";
import { Box, Stack, styled } from "@mui/material";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLedgerEnv } from "@/hooks/use-ledger-env";
import { useLedgerAccounts } from "@/hooks/use-ledger-accounts";
import { LEDGER_STATUS_OPTIONS } from "../data";
import { getLedgerAccountColumns } from "./_components/legder-account-column";
import CreateAccountDialog from "./_components/create-account";
import PostJournalDialog from "./_components/post-journal";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const LedgerAccounts = () => {
  const navigate = useNavigate();
  const { environment } = useLedgerEnv();
  const [createOpen, setCreateOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const statusFilter = searchParams.get("status") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);

  const { data, isLoading, isError } = useLedgerAccounts({
    environment,
    page: currentPage,
    size: pageSize,
    search: searchValue || undefined,
    status: statusFilter || undefined,
  });

  const handleView = (account) =>
    navigate(`/ledger/accounts/${account.accountNumber}`);

  return (
    <RootWrapper>
      <Card>
        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={3}>
            <CustomSearchInput
              paramKey="search"
              placeholder="Search by account number or name..."
            />
            <CustomSelectInput
              paramKey="status"
              label="Status"
              placeholder="All Statuses"
              options={LEDGER_STATUS_OPTIONS}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CustomButton
              variantType="outline"
              onClick={() => setJournalOpen(true)}
              sx={{ height: 35 }}
            >
              Post Journal
            </CustomButton>
            <CustomButton
              onClick={() => setCreateOpen(true)}
              sx={{ height: 35 }}
            >
              New Account
            </CustomButton>
          </Stack>
        </Stack>

        <DataTable
          data={data?.data ?? []}
          columns={getLedgerAccountColumns(handleView)}
          isLoading={isLoading}
          isError={isError}
        />

        <CustomPagination
          total={data?.totalCount}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>

      <CreateAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <PostJournalDialog
        open={journalOpen}
        onClose={() => setJournalOpen(false)}
      />
    </RootWrapper>
  );
};

export default LedgerAccounts;
